<template>
  <div
    class="notification-item relative bg-white light-bordered light-radius"
    :class="{ 'attention-shake': attentionAnimation }"
  >
    <q-btn
      class="close-btn absolute-top-right"
      icon="close"
      flat
      round
      dense
      color="grey-6"
      size="sm"
      :disable="isItemLoading"
      @click="handleCancel"
    />

    <div v-if="!isItemLoading" class="notification-content q-pa-sm">
      <div class="notification-icon">
        <q-icon :name="getNotificationIcon" color="primary" size="30px" />
        <p v-if="notification.count > 1" class="notification-badge text-warning">
          {{ notification.count }}
        </p>
      </div>

      <div class="notification-text border-bottom q-pb-sm">
        <p
          class="notification-type border-bottom q-pb-xs q-mb-sm text-12 text-brand text-font-medium"
        >
          {{ notificationTypeToShow }}
        </p>
        <p class="notification-title q-mb-xs text-16 text-primary">
          {{ getNotificationTitle }}
          <span class="text-primary text-font-bold">{{
            notification.companyName || notification.productName
          }}</span>
        </p>
        <p class="text-12 text-secondary text-font-light">
          {{ getNotificationSubtitle }}
        </p>
      </div>
      <div class="notification-actions q-mt-sm">
        <q-btn
          label="Close"
          color="secondary"
          size="sm"
          unelevated
          outline
          no-caps
          :disable="isItemLoading"
          padding=".25rem 1rem"
          class="refresh-btn"
          @click="handleCancel"
        />
        <q-btn
          :label="getActionButtonLabel"
          color="primary"
          size="sm"
          unelevated
          outline
          no-caps
          :disable="isItemLoading"
          padding=".25rem 1.5rem"
          class="refresh-btn q-ml-auto"
          @click="handleConfirm"
        />
      </div>
    </div>
    <div v-else class="notification-loader q-pa-sm">
      <q-spinner-dots color="primary" size="20px" />
      <p class="text-12 text-secondary text-font-light">Loading...</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useAuth } from 'src/composables/app/useAuth'
import { useConfirmUpdate } from 'src/composables/app/useConfirmUpdate'

import { getRelativeTime } from 'src/services/util.service'
import { log } from 'src/services/app/log.service'
import { notifyService } from 'src/services/app/notify.service'
import { NOTIFICATION_TYPES } from 'src/services/consts/api.const'
import { NOTIFICATION_MESSAGES } from 'src/services/consts/notification.const'
import { ROUTES } from 'src/services/consts/routes.const'

const props = defineProps({
  notification: {
    type: Object,
    required: true,
  },
})
const route = useRoute()
const { user } = useAuth()
const { removeUpdateNotification, createConfirmAction } = useConfirmUpdate()

const attentionAnimation = ref(false)
const isItemLoading = ref(false)
const currentTime = ref(new Date())
let timeUpdateInterval = null

// Update the current time every minute
onMounted(() => {
  timeUpdateInterval = setInterval(() => {
    currentTime.value = new Date()
  }, 60000)
})
onUnmounted(() => {
  if (timeUpdateInterval) {
    clearInterval(timeUpdateInterval)
  }
})
watch(
  () => props.notification.count,
  (newCount) => {
    if (newCount > 1) {
      triggerAttentionAnimation()
    }
  },
)

const getNotificationIcon = computed(() => {
  if (props.notification.type === NOTIFICATION_TYPES.AI_ASSESSMENT) {
    return 'auto_awesome'
  }
  return 'notification_important'
})
const notificationTypeToShow = computed(() => {
  let typeTxt = ''
  switch (props.notification.type) {
    case NOTIFICATION_TYPES.COMPANY_EDIT:
      typeTxt = 'Company'
      break
    case NOTIFICATION_TYPES.PRODUCT_EDIT:
      typeTxt = 'Product'
      break
    case NOTIFICATION_TYPES.AI_ASSESSMENT:
      typeTxt = 'AI Assessment'
      break
    default:
      typeTxt = 'Update'
      break
  }

  const updateTxt = props.notification.count > 1 ? 'Updates' : 'Update'
  return `${typeTxt} ${updateTxt}`
})
const getNotificationTitle = computed(() => {
  if (props.notification.type === NOTIFICATION_TYPES.AI_ASSESSMENT) {
    const action = props.notification.isStarting ? 'is starting' : 'has completed'
    return `Our AI ${action} the assessment on `
  }
  return `New data is now available for `
})
const getNotificationSubtitle = computed(() => {
  if (
    props.notification.type === NOTIFICATION_TYPES.AI_ASSESSMENT &&
    props.notification.sentById === user.value?.id
  ) {
    const status = props.notification.isStarting ? 'started' : 'completed'
    return `Assessment ${status} ${getRelativeTime(props.notification.updatedAt, currentTime)}`
  }
  return `Updated by ${props.notification.updatedBy} ${getRelativeTime(
    props.notification.updatedAt,
    currentTime,
  )}`
})
const isSameCompanyId = (a, b) => String(a) === String(b)

const getActionButtonLabel = computed(() => {
  const { companyId, productId } = props.notification
  const onCompanyPage = route.name === ROUTES.COMPANY_EDIT || route.name === ROUTES.COMPANY_INFO
  const isViewingSameCompany = onCompanyPage && isSameCompanyId(route.params.id, companyId)
  const isCompanyNotification = !productId
  const isViewingSameProduct = !!productId && isSameCompanyId(route.query.productId, productId)

  const canRefresh = isViewingSameCompany && (isCompanyNotification || isViewingSameProduct)

  return canRefresh ? 'Refresh' : 'View'
})

const triggerAttentionAnimation = async () => {
  attentionAnimation.value = true
  await nextTick()
  setTimeout(() => {
    attentionAnimation.value = false
  }, 600) // Match animation duration
}
const handleConfirm = async () => {
  try {
    isItemLoading.value = true

    const notification = props.notification
    const confirmAction = createConfirmAction(notification)
    // Create and execute the appropriate action based on notification type
    if (confirmAction) {
      await confirmAction()
      removeUpdateNotification(props.notification.id)
    }
  } catch (error) {
    log.error('Error during update:', error)
    isItemLoading.value = false
    notifyService.error(NOTIFICATION_MESSAGES.error.refreshData)
  }
}
const handleCancel = () => {
  removeUpdateNotification(props.notification.id)
}
</script>

<style lang="scss" scoped>
@import 'src/css/setup/_variables.scss';

.notification-item {
  z-index: 1;
  position: relative;
  box-shadow: $item-shadow;
  width: 100%;

  .close-btn {
    top: 0.25rem;
    right: 0.25rem;
  }

  .notification-content {
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: 0.5rem;
  }

  .notification-icon {
    grid-row: 1/-1;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
  }

  .notification-loader {
    min-height: 127px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 0.5rem;
  }

  .notification-badge {
    position: absolute;
    font-size: 10px;
    top: 0px;
    right: 0px;
    min-width: 18px;
    height: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid $clr-warning;
    background: white;
  }

  .notification-text {
    flex: 1;
    min-width: 0;
  }

  .notification-actions {
    grid-column: 2;
    flex-basis: 100%;

    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}

// Attention animations
.attention-shake {
  animation: attentionShake 0.6s ease-in-out;
}

@keyframes attentionShake {
  0% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-5px);
  }
  40% {
    transform: translateX(5px);
  }
  60% {
    transform: translateX(-3px);
  }
  80% {
    transform: translateX(3px);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
